import { transparentize } from 'polished';
import { FONT_FAMILY } from '@utils/constants';
import { deviceAxis, pcpDropAxis } from '@components/metrics-panel-hz/hooks/alerts/aiAlertUtils';
import { ANNOTATION_SERIES } from '@components/annotations/hooks/annotationOption';
import { METRIC_FILTER_SERIES } from './metricFilterSeriesOption';
import {
  DOD_SERIES,
  METRIC_SERIES,
  MANUAL_ALERT_SERIES,
  TIME_AXIS,
  VALUE_AXIS,
  DOD_TIME_AXIS,
} from './constants';
import { COMPARE_SERIES } from './compareSeriesOption';
import { AI_ALERT_MARKERS, AiAlertSeriesKeys } from '../hooks/alerts/constants';

// echarts rendering breaks if the type of the placeholder charts is not specified
// weridly if there's a "bar" type among line charts without data then entire axis and render gets messed up

const PRIMARY_SERIES = [
  {
    id: METRIC_SERIES,
    type: 'line',
  },
];

const SECONDARY_SERIES = [
  {
    id: METRIC_FILTER_SERIES,
    type: 'line',
  },
  {
    id: DOD_SERIES,
    type: 'line',
  },
];

const MARKER_SERIES = [
  {
    id: AI_ALERT_MARKERS,
    type: 'line',
  },
  {
    id: MANUAL_ALERT_SERIES,
    type: 'line',
  },
  {
    id: ANNOTATION_SERIES,
    type: 'line',
  },
];

const AI_ALERT_SERIES = AiAlertSeriesKeys.map((id) => ({ id, type: 'line' }));

const axisColor = '#ccc';
const axisLabelColor = '#000';

export const timeXAxis = {
  id: TIME_AXIS,
  type: 'time',
  show: true,
  splitNumber: 2,
  // boundaryGap: true,
  axisLabel: {
    color: axisLabelColor,
    fontSize: 12,
    fontFamily: FONT_FAMILY,
    hideOverlap: true,
    showMinLabel: true,
    showMaxLabel: true,
    alignMinLabel: 'left',
    alignMaxLabel: 'right',
  },
  splitLine: {
    show: false,
  },
  axisTick: {
    alignWithLabel: false,
    lineStyle: {
      color: axisColor,
    },
  },
  axisLine: {
    show: true,
    lineStyle: {
      color: axisColor,
    },
  },
};

const valueYAxis = {
  id: VALUE_AXIS,
  type: 'value',
  show: true,
  splitNumber: 1,
  nameLocation: 'middle',
  axisLabel: {
    color: axisLabelColor,
    fontSize: 12,
    fontFamily: FONT_FAMILY,
    hideOverlap: true,
    showMinLabel: true,
    showMaxLabel: true,
  },
  splitLine: {
    show: true,
  },
  axisTick: {
    show: false,
  },
  axisLine: {
    show: false,
    lineStyle: {
      color: axisColor,
    },
  },
};

export const chartOption = ({
  metric,
  allMetrics,
  appendTo = () => null, // document.getElementsByTagName('body')[0]
}) => {
  const metricInfo = allMetrics[metric] || {};
  return {
    id: metric,
    xAxis: [timeXAxis, { ...timeXAxis, id: DOD_TIME_AXIS, show: false }],
    yAxis: [valueYAxis, deviceAxis, pcpDropAxis],
    grid: {
      top: 10,
      bottom: 30,
      left: 53,
      right: 25,
    },
    series: [
      ...PRIMARY_SERIES,
      ...SECONDARY_SERIES,
      ...MARKER_SERIES,
      ...AI_ALERT_SERIES,
      ...COMPARE_SERIES,
    ],
    tooltip: {
      trigger: 'axis',
      textStyle: { fontFamily: FONT_FAMILY, fontSize: 12 },
      confine: true,
      appendTo,
      // appendToBody: true,
      className: 'echarts-tooltip',
    },
    dataZoom: [
      {
        type: 'slider',
        show: false,
        xAxisIndex: [0],
      },
      // dod x-axis
      {
        type: 'slider',
        show: false,
        xAxisIndex: 1,
      },
    ],
    toolbox: {
      show: false,
    },
    brush: {
      brushType: 'lineX',
      seriesIndex: [0],
      brushLink: [0],
      xAxisIndex: [0],
      transformable: false,
      throttleType: 'debounce',
      throttleDelay: 300,
      brushStyle: {
        borderWidth: 1,
        color: 'rgba(120,140,180,0.3)',
        borderColor: 'rgb(224, 224, 224,0.8)',
      },
    },
    legend: {
      show: false,
      type: 'scroll',
    },
    animation: true,
    _metric: metric,
    _title: metricInfo.displayName,
    _shortMetric: metricInfo.short,
    _subscript: metricInfo.subscript,
    _unit: metricInfo.unit,
    _viewType: 'timeseries',
  };
};

// immutability update diff
export const getBarOptionDiff = ({ filterColor }: any) => {
  return {
    series: [
      {
        $merge: {
          type: 'bar',
          barCategoryGap: '1%',
          itemStyle: {
            color: transparentize(0.75, filterColor),
            borderColor: filterColor,
          },
        },
      },
    ],
  };
};
